import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';
import { FaSave, FaSpinner, FaEdit, FaTrash, FaPlus, FaTimes, FaEye } from 'react-icons/fa';

const MediaManagement = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // null, 'featured', or 'author'
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category: '',
    featuredImage: '',
    authorName: '',
    authorImage: '',
    publishDate: ''
  });

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/media/public?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMedia(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const uploadImage = async (file, imageType) => {
    // Validate file type
    const allowedMimes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedMimes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(imageType);
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Authentication token not found. Please login again.');
        return;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      console.log('Uploading', imageType, 'to:', `${API_URL}/media/admin/upload/image`);
      const response = await axios.post(
        `${API_URL}/media/admin/upload/image`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        handleInputChange(
          imageType === 'featured' ? 'featuredImage' : 'authorImage',
          response.data.data.url
        );
        toast.success(`${imageType === 'featured' ? 'Featured' : 'Author'} image uploaded to Cloudinary successfully`);
      }
    } catch (error) {
      console.error(`Error uploading ${imageType} image:`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });

      if (error.response?.status === 401) {
        toast.error('Unauthorized. Please login again.');
      } else if (error.response?.status === 413) {
        toast.error('File is too large. Maximum size is 5MB.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid file format.');
      } else if (!error.response) {
        toast.error('Cannot connect to backend server. Make sure it\'s running on port 5000.');
      } else {
        toast.error(error.response?.data?.message || `Failed to upload ${imageType} image`);
      }
    } finally {
      setUploading(null);
    }
  };

  const handleFeaturedImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file, 'featured');
    }
    e.target.value = '';
  };

  const handleAuthorImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file, 'author');
    }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.content || !formData.category || 
        !formData.featuredImage || !formData.authorName || !formData.authorImage) {
      toast.error('All fields (including full content) are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const payload = {
        ...formData,
        content: formData.content || formData.description,
        publishDate: formData.publishDate ? new Date(formData.publishDate).toISOString() : new Date().toISOString()
      };

      if (editingId) {
        // Update
        const response = await axios.put(
          `${API_URL}/media/admin/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Media updated successfully');
          setEditingId(null);
        }
      } else {
        // Create
        const response = await axios.post(
          `${API_URL}/media/admin`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Media created successfully');
        }
      }

      resetForm();
      fetchMedia();
    } catch (error) {
      console.error('Error saving media:', error);
      toast.error(error.response?.data?.message || 'Failed to save media');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      description: item.description,
      content: item.content || '',
      category: item.category,
      featuredImage: item.featuredImage,
      authorName: item.authorName,
      authorImage: item.authorImage,
      publishDate: item.publishDate ? new Date(item.publishDate).toISOString().split('T')[0] : ''
    });
    setEditingId(item._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this media item?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/media/admin/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Media deleted successfully');
        fetchMedia();
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      category: '',
      featuredImage: '',
      authorName: '',
      authorImage: '',
      publishDate: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const filteredMedia = media.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.authorName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 pt-6 pb-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Media Management</h1>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <FaPlus /> Add New Media
          </button>
        </div>

        {/* Form Section */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Media' : 'Create New Media'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter media title"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Sports, News, Tips"
                    required
                  />
                </div>

                {/* Author Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author Name *
                  </label>
                  <input
                    type="text"
                    value={formData.authorName}
                    onChange={(e) => handleInputChange('authorName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter author name"
                    required
                  />
                </div>

                {/* Publish Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) => handleInputChange('publishDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                  placeholder="Enter media description"
                  required
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Media Content (Full Text / HTML) *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="8"
                  placeholder="Enter the full media article content here (HTML supported)..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Word count: {formData.content ? formData.content.split(/\s+/).filter(w => w).length : 0}
                </p>
              </div>

              {/* Featured Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image *</label>
                <div className="space-y-3">
                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition">
                    <label className="flex flex-col items-center justify-center cursor-pointer">
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Upload to Cloudinary</p>
                        <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF, WebP (max 5MB)</p>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleFeaturedImageUpload}
                        disabled={uploading === 'featured'}
                        className="hidden"
                      />
                      {uploading === 'featured' && <FaSpinner className="animate-spin text-blue-600 mt-2" />}
                      {uploading !== 'featured' && <span className="text-blue-600 text-sm mt-2">Click to upload</span>}
                    </label>
                  </div>

                  {/* URL Input */}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Or paste image URL</label>
                    <input
                      type="url"
                      value={formData.featuredImage}
                      onChange={(e) => handleInputChange('featuredImage', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {/* Image Preview */}
                  {formData.featuredImage && (
                    <div className="flex items-center gap-4">
                      <img
                        src={formData.featuredImage}
                        alt="Featured Preview"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWVlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange('featuredImage', '')}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Author Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Author Image *</label>
                <div className="space-y-3">
                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition">
                    <label className="flex flex-col items-center justify-center cursor-pointer">
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Upload to Cloudinary</p>
                        <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF, WebP (max 5MB)</p>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleAuthorImageUpload}
                        disabled={uploading === 'author'}
                        className="hidden"
                      />
                      {uploading === 'author' && <FaSpinner className="animate-spin text-blue-600 mt-2" />}
                      {uploading !== 'author' && <span className="text-blue-600 text-sm mt-2">Click to upload</span>}
                    </label>
                  </div>

                  {/* URL Input */}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Or paste image URL</label>
                    <input
                      type="url"
                      value={formData.authorImage}
                      onChange={(e) => handleInputChange('authorImage', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/author.jpg"
                    />
                  </div>

                  {/* Image Preview */}
                  {formData.authorImage && (
                    <div className="flex items-center gap-4">
                      <img
                        src={formData.authorImage}
                        alt="Author Preview"
                        className="w-24 h-24 object-cover rounded-full border border-gray-200"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5FcnJvcjwvdGV4dD48L3N2Zz4=';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange('authorImage', '')}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                  {saving ? 'Saving...' : 'Save Media'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search media by title, category, or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="animate-spin text-blue-600 text-3xl" />
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500 text-lg">No media items found. Create your first media item!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredMedia.map((item) => (
              <div key={item._id} className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition">
                <div className="flex gap-4">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={item.featuredImage}
                      alt={item.title}
                      className="w-24 h-24 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='12' fill='%236b7280'%3EMedia%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{item.description.substring(0, 100)}...</p>
                        <div className="flex gap-4 mt-2 text-sm text-gray-600">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {item.category}
                          </span>
                          <span>By {item.authorName}</span>
                          <span>{new Date(item.publishDate).toLocaleDateString()}</span>
                          <span>{item.viewCount} views</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedMedia(item)}
                          className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition"
                          title="View Details"
                        >
                          <FaEye size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {!loading && media.length > 0 && (
          <div className="mt-6 text-sm text-gray-600">
            Showing {filteredMedia.length} of {media.length} media items
          </div>
        )}

        {/* Media Details Modal */}
        {selectedMedia && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto flex flex-col border border-slate-100 animate-fade-in">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b border-slate-100">
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                  {selectedMedia.category}
                </span>
                <button
                  onClick={() => setSelectedMedia(null)}
                  className="text-slate-400 hover:text-slate-600 transition p-1 hover:bg-slate-50 rounded-lg"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Featured Image */}
                <div className="w-full h-64 overflow-hidden rounded-xl bg-slate-100">
                  <img
                    src={selectedMedia.featuredImage}
                    alt={selectedMedia.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='12' fill='%236b7280'%3EMedia%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>

                {/* Title */}
                <h2 className="text-2xl font-black text-slate-800 tracking-tight leading-snug">
                  {selectedMedia.title}
                </h2>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-slate-500 border-y border-slate-50 py-3">
                  <span className="flex items-center gap-1.5">
                    By <span className="text-slate-800">{selectedMedia.authorName}</span>
                  </span>
                  <span className="text-slate-300">•</span>
                  <span>Published {new Date(selectedMedia.publishDate).toLocaleDateString()}</span>
                  <span className="text-slate-300">•</span>
                  <span>{selectedMedia.viewCount || 0} Views</span>
                </div>

                {/* Description */}
                <div className="text-sm text-slate-600 leading-relaxed font-medium space-y-4">
                  <p className="font-bold text-slate-800 text-xs uppercase tracking-wider text-slate-400">Short Description</p>
                  <p className="whitespace-pre-wrap">{selectedMedia.description}</p>
                  
                  {selectedMedia.content && (
                    <div className="border-t border-slate-100 pt-4">
                      <p className="font-bold text-slate-800 text-xs uppercase tracking-wider text-slate-400 mb-2">Full Content (HTML Preview)</p>
                      <div 
                        className="rich-media-content prose max-w-none border border-slate-100 p-4 bg-slate-50 rounded-xl max-h-60 overflow-y-auto text-xs"
                        dangerouslySetInnerHTML={{ __html: selectedMedia.content }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2 rounded-b-2xl">
                <button
                  onClick={() => {
                    const mediaToEdit = selectedMedia;
                    setSelectedMedia(null);
                    handleEdit(mediaToEdit);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-md shadow-blue-500/10 transition active:scale-[0.98]"
                >
                  Edit Post
                </button>
                <button
                  onClick={() => setSelectedMedia(null)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-xl text-xs transition active:scale-[0.98]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default MediaManagement;
